import { useState } from 'react';
import { setupAPI } from '../../api/client';
import { DEFAULT_MODELS } from '../../constants';
import type { AIProvider } from '../../types';

const PROVIDER_OPTIONS: { value: AIProvider; label: string }[] = [
  { value: 'managed', label: 'Clawer AI' },
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'ollama', label: 'Ollama (local)' },
];

interface AIProviderStepProps {
  provider: AIProvider;
  apiKey: string;
  model: string;
  ollamaUrl: string;
  keyValidated: boolean;
  onProviderChange: (provider: AIProvider) => void;
  onApiKeyChange: (key: string) => void;
  onModelChange: (model: string) => void;
  onOllamaUrlChange: (url: string) => void;
  onKeyValidated: (valid: boolean) => void;
  onNext: () => void;
  onBack: () => void;
}

export function AIProviderStep({
  provider,
  apiKey,
  model,
  ollamaUrl,
  keyValidated,
  onProviderChange,
  onApiKeyChange,
  onModelChange,
  onOllamaUrlChange,
  onKeyValidated,
  onNext,
  onBack,
}: AIProviderStepProps) {
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [showSkipWarning, setShowSkipWarning] = useState(false);

  function handleProviderChange(newProvider: AIProvider) {
    onProviderChange(newProvider);
    onModelChange(DEFAULT_MODELS[newProvider]);
    onKeyValidated(false);
    setValidationError('');
    setShowSkipWarning(false);
  }

  async function handleValidate() {
    setValidating(true);
    setValidationError('');
    try {
      const body: { provider: string; api_key?: string; ollama_url?: string } = { provider };
      if (provider === 'ollama') {
        body.ollama_url = ollamaUrl;
      } else if (provider !== 'managed') {
        body.api_key = apiKey;
      }
      const result = await setupAPI.validateKey(body);
      onKeyValidated(result.valid);
      if (result.valid) {
        setShowSkipWarning(false);
      } else {
        setValidationError(result.error || (provider === 'managed' ? 'Invalid license key' : 'Invalid API key'));
      }
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : 'Validation failed');
      onKeyValidated(false);
    } finally {
      setValidating(false);
    }
  }

  function handleNext() {
    // Ollama doesn't need key validation
    if (provider === 'ollama') {
      onNext();
      return;
    }

    if (provider === 'managed') {
      onNext();
      return;
    }

    if (!apiKey) {
      setValidationError('API key is required for Clawer to analyse your conversations.');
      return;
    }

    if (!keyValidated) {
      setShowSkipWarning(true);
      return;
    }

    onNext();
  }

  function handleForceNext() {
    setShowSkipWarning(false);
    onNext();
  }

  const isOllama = provider === 'ollama';
  const isManaged = provider === 'managed';

  return (
    <div>
      <p className="text-xs font-medium text-ink-tertiary uppercase tracking-wider mb-1" data-ph-no-mask>
        Step 2 of 4
      </p>
      <h1 className="text-2xl font-bold text-ink mb-1" data-ph-no-mask>AI Provider</h1>
      <p className="text-sm text-ink-secondary mb-6 leading-relaxed" data-ph-no-mask>
        Choose your AI provider and enter your API key. Clawer uses this to analyse your conversations.
      </p>

      {/* Provider select */}
      <label className="block text-sm font-medium text-ink mb-1" data-ph-no-mask>Provider</label>
      <select
        value={provider}
        onChange={(e) => handleProviderChange(e.target.value as AIProvider)}
        className="w-full px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20 mb-4"
      >
        {PROVIDER_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      {/* Auto-configured (managed) */}
      {isManaged ? (
        <div className="mb-4 p-4 rounded-lg bg-canvas border border-edge">
          <div className="flex items-center gap-2">
            <span className="material-icons-round text-success text-lg">check_circle</span>
            <div>
              <p className="text-sm font-medium text-ink">Auto-configured</p>
              <p className="text-xs text-ink-secondary">
                Your AI provider is managed by your Clawer subscription. No setup needed.
              </p>
            </div>
          </div>
        </div>
      ) : !isOllama ? (
        /* API Key (cloud providers) */
        <div className="mb-4">
          <label className="block text-sm font-medium text-ink mb-1" data-ph-no-mask>API Key</label>
          <div className="flex gap-2">
            <input
              type="password"
              value={apiKey}
              onChange={(e) => { onApiKeyChange(e.target.value); onKeyValidated(false); setShowSkipWarning(false); }}
              placeholder="Enter your API key"
              className="flex-1 px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
            <button
              onClick={handleValidate}
              disabled={validating || !apiKey}
              className="px-3 py-2 rounded-lg border border-edge text-sm font-medium text-ink hover:bg-canvas transition-colors disabled:opacity-50"
            >
              {validating ? (
                <span className="material-icons-round text-sm animate-spin">sync</span>
              ) : (
                'Validate'
              )}
            </button>
          </div>
          {/* Validation status */}
          {keyValidated && (
            <p className="text-xs text-success mt-1 flex items-center gap-1" data-ph-no-mask>
              <span className="material-icons-round text-xs">check_circle</span>
              API key is valid
            </p>
          )}
          {validationError && !showSkipWarning && (
            <p className="text-xs text-error mt-1">{validationError}</p>
          )}
        </div>
      ) : null}

      {/* Ollama URL */}
      {isOllama && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-ink mb-1" data-ph-no-mask>Ollama URL</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={ollamaUrl}
              onChange={(e) => onOllamaUrlChange(e.target.value)}
              placeholder="http://localhost:11434"
              className="flex-1 px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
            <button
              onClick={handleValidate}
              disabled={validating}
              className="px-3 py-2 rounded-lg border border-edge text-sm font-medium text-ink hover:bg-canvas transition-colors disabled:opacity-50"
            >
              {validating ? (
                <span className="material-icons-round text-sm animate-spin">sync</span>
              ) : (
                'Test'
              )}
            </button>
          </div>
          {keyValidated && (
            <p className="text-xs text-success mt-1 flex items-center gap-1" data-ph-no-mask>
              <span className="material-icons-round text-xs">check_circle</span>
              Connected to Ollama
            </p>
          )}
          {validationError && (
            <p className="text-xs text-error mt-1">{validationError}</p>
          )}
        </div>
      )}

      {/* Model */}
      <label className="block text-sm font-medium text-ink mb-1" data-ph-no-mask>Model</label>
      <input
        type="text"
        value={model}
        onChange={(e) => onModelChange(e.target.value)}
        placeholder="Model name"
        className="w-full px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20 mb-4"
      />

      {/* Skip warning */}
      {showSkipWarning && (
        <div className="rounded-lg bg-warning-bg border border-warning/20 p-3 mb-4">
          <p className="text-xs text-warning font-medium" data-ph-no-mask>
            API key not validated yet
          </p>
          <p className="text-xs text-ink-secondary mt-1" data-ph-no-mask>
            You can continue without validating, but the AI features won&apos;t work if the key is wrong.
          </p>
          <button
            onClick={handleForceNext}
            className="mt-2 text-xs font-medium text-warning hover:text-warning/80 underline"
            data-ph-no-mask
          >
            Continue anyway
          </button>
        </div>
      )}

      {/* Navigation */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={onBack}
          className="px-4 py-2.5 rounded-lg border border-edge text-sm font-medium text-ink hover:bg-canvas transition-colors"
          data-ph-no-mask
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="px-5 py-2.5 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-deep transition-colors"
          data-ph-no-mask
        >
          Next
        </button>
      </div>
    </div>
  );
}
